// Commercial-intent classifier for blog posts, derived from the slug.
//
// Used to decide how early to surface the product card in the reading flow:
// buying-intent posts ("best X", "X vs Y", "X review") surface the pick after
// H2 #1 because the product IS the answer the reader searched for; informational
// posts get it after H2 #2 (post-value, less interruptive). Both the server-side
// product-marker injection (lib/content-processor.ts) and the inline tool-CTA
// position (components/blog/blog-post-main-grid.tsx) read this so they stay in sync.
//
// We classify on the slug (not the title) because the slug is already threaded
// through processContentForContext + its cache key, and slugs carry the same
// commercial tokens as titles ("best-...", "-vs-", "...-review", "...-worth-it").

import { ARTICLE_PRODUCT_PICKS } from '@/data/article-product-picks'
import {
  h2Offsets,
  occupiedWidgetSlots,
  firstFreeSlotFrom,
} from '@/lib/blog-widget-placement'

// Slug tokens that signal buying intent. Hyphen-delimited because slugs are kebab-case.
const COMMERCIAL_SLUG_RE = /(^|-)(best|review|worth-it|top-picks|buyers?-guide|which-(one|simple))(-|$)|-vs-/

// Explicit overrides for slugs the regex misclassifies. Keep this small.
// - allow: commercial despite no obvious token (product instruction/comparison posts that monetize)
// - deny: matches a commercial token but reads informational (don't surface the pick aggressively)
const COMMERCIAL_OVERRIDES: Record<string, boolean> = {
  // Reads non-commercial but is a high-intent product-application post.
  'often-you-apply-scotts-weed-feed': true,
  // Match a commercial token ("-best-") but read informational — a material-comparison
  // guide and a monthly-schedule guide, neither naming a buyable product. Deny so the
  // generic card takes the post-value slot instead of snapping aggressively above H2 #1.
  'most-common-top-dressing-compost-peat-moss-topsoil-sand-which-is-best-for-you': false,
  'transition-zone-lawn-care-best-grass-monthly-schedule': false,
  // Single-product application/instruction guides: no best/vs/review token, but the whole
  // post is about applying ONE specific product the reader likely intends to buy. Allow so
  // their curated pick surfaces at peak intent (above H2 #1) like the weed-feed sibling above,
  // instead of depth-anchoring at ~18%.
  'ortho-weedclear-lawn-weed-killer-how-to-use': true,
  'tenacity-herbicide-mesotrione-application-guide': true,
  'milorganite-application-guide-apply': true,
  'scotts-southern-turf-builder-instructions': true,
  'scotts-turf-builder-halts-crabgrass-preventer-instructions': true,
  'scotts-turf-builder-triple-action-instructions-complete-application-guide': true,
}

export function isCommercialIntent(slug: string | undefined | null): boolean {
  if (!slug) return false
  if (Object.hasOwn(COMMERCIAL_OVERRIDES, slug)) return COMMERCIAL_OVERRIDES[slug]
  return COMMERCIAL_SLUG_RE.test(slug)
}

// H2 boundary (1-indexed) for the inline tool CTA. The LEAD product card is depth-anchored in
// the content (not at an H2 — see insertProductPickMarkers), so the CTA can never stack with it
// at a heading. We only avoid SECONDARY curated picks' H2 boundaries, and start the search below
// the early lead: commercial leads sit ~10% deep so the CTA goes mid-article (H2 #2+);
// informational leads sit ~18% deep so the tool CTA can lead at H2 #1. If the returned index
// exceeds the post's H2 count, BlogContentWithTOC skips that injection gracefully.
export function toolCtaH2(slug: string | undefined | null): number {
  const picks = slug ? ARTICLE_PRODUCT_PICKS[slug] : undefined
  // Drop the lead (earliest) pick — it's depth-anchored, not at an H2 — and avoid the rest.
  const secondaryH2s = picks?.length
    ? [...picks].map((p) => p.insertAfterH2).sort((a, b) => a - b).slice(1)
    : []
  const used = new Set<number>(secondaryH2s)
  let n = isCommercialIntent(slug) ? 2 : 1
  while (used.has(n)) n++
  return n
}

// Slug/category/tag tokens that mark a post as product-intent — the gate for the
// product-guide lead magnet. Moved here from blog-content-with-toc.tsx so the
// server component (blog-post-main-grid) and the client renderer agree on the
// gate without duplicating the list.
const PRODUCT_GUIDE_INTENT_TERMS = [
  'fertiliz', 'weed', 'herbicide', 'pre-emergent', 'pre emergent',
  'post-emergent', 'post emergent', 'crabgrass', 'preventer', 'weed-and-feed',
  'weed feed', 'tenacity', 'prodiamine', 'dithiopyr', 'mesotrione', 'seed',
  'seeding', 'overseed', 'starter', 'soil test', 'soil-test', 'sprayer',
  'spreader', 'mower', 'mowing', 'watering', 'irrigation', 'sprinkler',
  'equipment', 'milorganite', 'scotts', 'npk', '10-10-10', 'winterizer',
  'aeration', 'aerator',
]

// Posts where the product-guide lead magnet outranks the tool CTA. Curated from
// the 2026-07-18 placement audit (GSC + PostHog): these are the site's
// highest-traffic near-100%-purchase-intent posts (~18k visitors/60d combined),
// yet the magnet was being squeezed out by its lowest-priority slot ordering.
// On these slugs the magnet places FIRST (from H2 #2) and the tool CTA yields.
const PRODUCT_GUIDE_PRIORITY_SLUGS = new Set([
  // Scotts weed-and-feed cluster (top traffic, mid-purchase readers)
  'scotts-weed-and-feed-application-guide-best-products',
  'long-scotts-weed-feed-take-work',
  'often-you-apply-scotts-weed-feed',
  'scotts-weed-feed-actually-work-honest-review',
  'scotts-weed-and-feed-buyers-guide-which-one-to-buy-2026',
  'scotts-turf-builder-triple-action-instructions-complete-application-guide',
  // Product application guides (owners who rebuy/upgrade)
  'spectracide-weed-stop-instructions-use-best-results',
  'tenacity-herbicide-mesotrione-application-guide',
  '10-10-10-fertilizer-when-how-to-apply',
  'milorganite-application-guide-best-uses-2026',
  // "Best fertilizer" cluster (purest commercial intent, best positions)
  'best-fertilizer-centipede-grass-2026-guide',
  'best-fertilizer-for-bermuda-grass-in-texas',
  'best-fertilizer-for-bermuda-grass-top-picks-reviews',
  'best-fertilizer-for-bermuda-grass-in-georgia',
  'best-fertilizer-for-st-augustine-grass-2026-guide',
  'best-fertilizer-for-zoysia-grass-2026-guide',
  'best-fertilizer-kentucky-bluegrass-2026-guide',
  'best-summer-lawn-fertilizers-keep-grass-green-healthy',
])

export function isProductGuidePrioritySlug(slug: string): boolean {
  return PRODUCT_GUIDE_PRIORITY_SLUGS.has(slug)
}

// Early-CTA pilot (item 1.5): a per-slug allowlist that earns ONE intent-matched
// CTA near the top of the post (before H2 #1), in addition to the mid-content
// contextual CTA. Modeled on PRODUCT_GUIDE_PRIORITY_SLUGS. These are top-traffic
// diagnose/identify/tool-intent posts; commercial posts already carrying the
// early product-guide magnet at H2 #2 (PRODUCT_GUIDE_PRIORITY_SLUGS) are
// deliberately EXCLUDED so we never stack two early elements. This list is a
// code-derived proxy from the reminder-widget + priority clusters and should be
// re-cut against fresh GSC clicks later.
// Every slug verified against published Directus posts on 2026-07-25 (the
// original draft list contained guessed slugs that matched no real post, so the
// pilot silently no-op'd). Members must ALSO be pick-free in
// ARTICLE_PRODUCT_PICKS — insertEarlyCtaMarker skips any post with a curated
// pick, so an allowlisted post with picks never renders the early CTA.
// Lives here rather than in content-processor so the client-side CTA component
// can read it for the `early_cta_post` cohort property without pulling the
// whole server-side content pipeline into the browser bundle.
export const EARLY_CTA_POSTS = new Set<string>([
  // Diagnose-intent problem posts (symptom → photo diagnose flow)
  'bermuda-grass-diseases-identification-treatment',
  'fairy-ring-lawn-causes-treatment',
  'red-thread-lawn-disease-identification-treatment',
  'pythium-blight-lawn-disease-identification-treatment',
  'summer-patch-lawn-disease-identification-treatment',
  'leaf-spot-melting-out-lawn-disease-treatment',
  'how-to-get-rid-of-chinch-bugs',
  'sod-webworms-lawn-damage-control',
  'mole-crickets-identify-control',
  'signs-of-overwatering-grass',
  // Grass-identity posts (which grass → grass-ID flow)
  'types-of-grass-in-texas',
  'types-of-grass-in-florida',
  'types-of-grass-in-georgia',
  'meyer-zoysia-identification-care-tips',
  // Tool-intent care posts (calculator flow)
  'pre-emergent-timing-when-to-apply-guide',
  'how-often-to-water-based-on-grass-type',
  'lawn-watering-schedule-guide',
])

/** True when a slug is in the early-CTA pilot allowlist. */
export function isEarlyCtaPost(slug: string): boolean {
  return EARLY_CTA_POSTS.has(slug)
}

export function isProductGuideIntent(
  slug: string,
  category: string,
  tags?: string[],
): boolean {
  const haystack = [slug, category, ...(tags ?? [])]
    .join(' ')
    .toLowerCase()
    .replace(/[_/]+/g, ' ')
  return PRODUCT_GUIDE_INTENT_TERMS.some((term) => haystack.includes(term))
}

/**
 * Slug tokens that mark a post as "reader is trying to work out WHICH grass they
 * have" — the audience the photo identifier serves directly.
 *
 * Single source of truth: the mid-content grass-type branch in
 * components/blog/contextual-tool-cta.tsx reads this list, and so does the
 * post-read slot in components/blog/blog-post-footer-sections.tsx. Keeping one
 * list stops the two slots from disagreeing about what an identify post is.
 */
export const GRASS_IDENTITY_TERMS = [
  'grass-type', 'augustine', 'bermuda', 'zoysia', 'fescue', 'bluegrass',
] as const

export function isGrassIdentityIntent(
  slug: string | undefined | null,
  category?: string | null,
): boolean {
  const s = slug?.toLowerCase() ?? ''
  const c = category?.toLowerCase() ?? ''
  return GRASS_IDENTITY_TERMS.some((term) => s.includes(term)) || c === 'grass types'
}

/**
 * Buying tokens WITHOUT `-vs-`. See resolvePostReadSurface for why the two are
 * split: "X vs Y" is a purchase question between products and an identification
 * question between grasses, and the same regex cannot tell those apart.
 */
const BUYING_SLUG_RE = /(^|-)(best|review|worth-it|top-picks|buyers?-guide|which-(one|simple))(-|$)/

/**
 * Is this reader trying to work out which grass they have?
 *
 * Broader than isGrassIdentityIntent, and deliberately NOT shared with the
 * mid-content CTA branch: that branch's token list is byte-identical to what it
 * has always been, and widening it there would silently re-bucket a live
 * instrumented surface. This one only feeds the post-read slot, which is new.
 *
 * The extra coverage is the `types-of-grass-in-{state}` series. Those 20 posts
 * are the most literal "which grass do I have" audience on the site, and they
 * matched NOTHING: their slug reads `types-of-grass-in-…` (no `grass-type`
 * token) and their category is `Climate & Regional Tips`, not `Grass Types`.
 * Verified against Directus, 2026-07-22.
 */
export function isIdentifyAudience(
  slug: string | undefined | null,
  category?: string | null,
): boolean {
  const s = slug?.toLowerCase() ?? ''
  const c = category?.toLowerCase() ?? ''
  if (s.includes('types-of-grass')) return true
  // Looser than the exact `=== 'grass types'` match so category naming drift
  // ("Grass Types Guide", "Grass Type") does not silently drop the surface.
  if (c.includes('grass type')) return true
  return isGrassIdentityIntent(slug, category)
}

/**
 * Turf-problem intent: is this post about a lawn DISEASE (fungal) or a lawn
 * PEST (insect)?
 *
 * The product-recommendation API ranks purely on lexical overlap, so the token
 * "lawn" in a product name out-scored everything else and put a broadleaf weed
 * killer in the "Recommended Products" block on a gray leaf spot page. Weed
 * killers and fertilizers do nothing for a fungus or a grub, so those posts need
 * a hard class gate, not a ranking nudge. This detector is the article side of
 * it; lib/product-keyword-scoring.ts holds the product side.
 *
 * Detection is on the slug + tags (and, for the category mapper, the blog
 * category), NOT on the Directus blog category. `Pest Control` is the category
 * of several WEED posts (`fall-pre-emergent-timing-guide`,
 * `common-lawn-weeds-and-how-to-remove-them`), so trusting it would put
 * insecticides on a crabgrass pre-emergent guide.
 *
 * Every token carries word boundaries on purpose. Bare `patch`/`spot`/`rot`
 * would classify `fix-patchy-lawn-spots-after-winter` (a seed-and-repair post)
 * as a disease, and the tag "brown patchy grass" on the armyworm post would do
 * the same; `\bbrown patch\b` does not match "brown patchy".
 */
export type TurfProblemIntent = 'disease' | 'pest' | 'both' | null

// `disease\w*` on purpose: it has to catch "diseases" and the product name
// "DiseaseEx", which is the only problem token on the Scotts DiseaseEx slug.
const TURF_DISEASE_RE =
  /\b(disease\w*|fungus|fungal|fungicides?|blight|rust|anthracnose|pythium|rhizoctonia|gaeumannomyces|pyricularia|azoxystrobin|propiconazole|chlorothalonil|myclobutanil|tebuconazole|thiophanate|dollar spots?|leaf spots?|gr[ae]y leaf|brown patch|large patch|summer patch|spring dead spot|snow mold|lawn mold|root rot|take all|fairy rings?|red thread|melting out|necrotic ring|slime mold|powdery mildew)\b/g

const TURF_PEST_RE =
  /\b(grubs?|chinch|webworms?|armyworms?|cutworms?|mole crickets?|billbugs?|insects?|insecticides?|fire ants?|lawn pests?|bifenthrin|imidacloprid|carbaryl|trichlorfon|chlorantraniliprole|spinosad)\b/g

function problemHaystack(parts: Array<string | null | undefined>): string {
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function countMatches(re: RegExp, haystack: string): number {
  re.lastIndex = 0
  return (haystack.match(re) ?? []).length
}

/**
 * Classify a post as disease-intent, pest-intent, both, or neither.
 *
 * Whichever class has more token hits wins. A genuine tie with hits on both
 * sides returns 'both' rather than picking one, because that is what a post like
 * `brown-spots-lawn-summer-drought-disease-or-grubs` actually is: the reader
 * does not yet know which problem they have, and either treatment class is a
 * legitimate recommendation.
 */
export function turfProblemIntent(
  slug: string | undefined | null,
  category?: string | null,
  tags?: string[] | null,
): TurfProblemIntent {
  const hay = problemHaystack([slug, category, ...(tags ?? [])])
  if (!hay) return null
  const disease = countMatches(TURF_DISEASE_RE, hay)
  const pest = countMatches(TURF_PEST_RE, hay)
  if (disease === 0 && pest === 0) return null
  if (pest === 0) return 'disease'
  if (disease === 0) return 'pest'
  if (disease === pest) return 'both'
  return disease > pest ? 'disease' : 'pest'
}

/** What, if anything, a post shows at the post-read peak. */
export type PostReadSurface = 'diagnose' | 'weed' | 'scan' | 'none'

/**
 * Single decision for the post-read slot: which ONE surface a post earns after
 * the reader finishes it.
 *
 * Precedence is deliberate:
 *  1. diagnose / weed — the reader has a visible problem, and the photo flows
 *     that resolve it are the best-converting capture surfaces on the site.
 *  2. none, on purchase-intent posts — these ALREADY carry the product-guide
 *     lead magnet mid-content (see resolveInlineCtaSlots below), so repeating it
 *     here would render the same card twice on one page. This branch exists to
 *     SUPPRESS the scan CTA on them: a "best fertilizer for bermuda grass"
 *     reader matches the grass tokens too, but they are buying, not identifying.
 *  3. scan — the reader is still working out which grass they have, and nothing
 *     else on the post asks them to.
 *  4. none — show nothing. A dead ask still costs attention and stacks fatigue
 *     against the surfaces that work. The blanket newsletter ask that used to
 *     live here took 430 impressions and produced ZERO captures in the 14 days
 *     to 2026-07-22, which is what motivated this routing.
 *
 * `isDiagnosis` / `isWeed` are injected rather than imported so this stays a
 * pure decision function and the caller keeps ownership of those detectors.
 */
export function resolvePostReadSurface(
  slug: string,
  category: string,
  tags: string[] | undefined,
  flags: { isDiagnosis: boolean; isWeed: boolean },
): PostReadSurface {
  if (flags.isDiagnosis) return 'diagnose'
  if (flags.isWeed) return 'weed'
  if (isProductGuidePrioritySlug(slug)) return 'none'

  const identifies = isIdentifyAudience(slug, category)

  // What counts as "this reader is buying" depends on who they are.
  //
  // isCommercialIntent treats `-vs-` as commercial, which is right for
  // `electric-lawn-mowers-vs-gas` and wrong for
  // `perennial-ryegrass-vs-tall-fescue` — someone comparing two GRASSES is
  // asking which one they have, the best scan candidate on the site. For that
  // audience only the explicit buying tokens suppress.
  //
  // Suppression stays paired with isProductGuideIntent, which is loose on
  // purpose (it matches a tag like "mowing" or "watering"), so it must never be
  // the only signal: alone it killed the scan CTA on 6 of 7 candidate posts.
  const buying = identifies ? BUYING_SLUG_RE.test(slug) : isCommercialIntent(slug)
  if (buying && isProductGuideIntent(slug, category, tags)) return 'none'

  return identifies ? 'scan' : 'none'
}

/**
 * Resolve the H2 slots for the two CLIENT-injected inline CTAs — the contextual
 * tool CTA and the product-guide lead magnet — against the already-processed
 * blog HTML.
 *
 * The processed HTML already carries every SERVER-injected marker (product
 * picks, reminder widget, service-quote card) plus any hand-authored embeds, so
 * we read those as occupancy and place the two client CTAs in the first free
 * section at/after their preferred boundary. This closes the last gap in the
 * stacking fix: without it, the tool CTA still injected at a fixed H2 that a
 * server marker or embed already owned (the 4th card in the 2026-07 stack).
 *
 * Priority: on PRODUCT_GUIDE_PRIORITY_SLUGS the product-guide magnet places
 * first (from H2 #2) and the tool CTA yields; everywhere else the tool CTA
 * places first and the magnet drops when no section is free (its historical
 * "skip if no room" behavior). Both rank below the server markers, which are
 * already placed.
 *
 * Return contract matches BlogContentWithTOC's props:
 *  - inlineCTAAfterH2: a slot in range, or `h2Count + 1` (out of range) which the
 *    renderer skips gracefully via computeSplitPoint returning null.
 *  - productGuideAfterH2: a free slot, or undefined to skip.
 *
 * Cross-module invariant this relies on: BlogContentWithTOC renders against
 * `useTableOfContents(content).processedContent`, which is `injectHeadingIds` —
 * that only adds an `id=""` attribute to each existing heading, never adding,
 * removing, or reordering an <h2>. So the <h2> COUNT and ORDER match this
 * function's view, and because we return INDICES (not byte offsets) the extra
 * `id` bytes are harmless. Do not change injectHeadingIds to restructure
 * headings without revisiting this.
 */
export function resolveInlineCtaSlots(
  html: string,
  slug: string,
  category: string,
  tags?: string[],
): { inlineCTAAfterH2: number; productGuideAfterH2: number | undefined } {
  const h2s = h2Offsets(html)
  const h2Count = h2s.length
  const occupied = occupiedWidgetSlots(html, h2s)

  // On priority-audited commercial posts the product-guide magnet claims its
  // slot FIRST (early, from H2 #2) and the tool CTA takes what's left. On all
  // other posts the historical ordering holds: tool CTA first, magnet drops
  // when no section is free.
  const priority = isProductGuidePrioritySlug(slug)
  let productGuideAfterH2: number | undefined

  if (priority && isProductGuideIntent(slug, category, tags)) {
    const pgSlot = firstFreeSlotFrom(2, occupied, h2Count)
    if (pgSlot !== null) {
      productGuideAfterH2 = pgSlot
      occupied.add(pgSlot)
    }
  }

  // Tool CTA — seed with the intent-based preference, then nudge off occupancy.
  const toolSlot = firstFreeSlotFrom(toolCtaH2(slug), occupied, h2Count)
  const inlineCTAAfterH2 = toolSlot !== null ? toolSlot : h2Count + 1
  if (toolSlot !== null) occupied.add(toolSlot)

  if (!priority && isProductGuideIntent(slug, category, tags)) {
    const pgSlot = firstFreeSlotFrom(4, occupied, h2Count)
    if (pgSlot !== null) {
      productGuideAfterH2 = pgSlot
      occupied.add(pgSlot)
    }
  }

  return { inlineCTAAfterH2, productGuideAfterH2 }
}
